import { Input as BaseInput } from '@base-ui/react/input';
import type { ComponentProps, FC, ReactNode } from 'react';
import { cn } from './react';
import type { FormControlProps } from './types';

export type InputProps = FormControlProps<string> & {
  size: 8 | 10 | 12 | 16;
  fab?: boolean;
  block?: boolean;
  suffix?: ReactNode;
  prefix?: ReactNode;
  className?: string;
  placeholder?: string;
  type?: ComponentProps<'input'>['type'];
  inputMode?: ComponentProps<'input'>['inputMode'];
  dir?: ComponentProps<'input'>['dir'];
  readOnly?: boolean;
  min?: number;
  minLength?: number;
  max?: number;
  maxLength?: number;
  step?: number | string;
};

export const Input: FC<InputProps> = ({
  size,
  fab,
  block,
  className,
  suffix,
  prefix,
  onChange,
  value,
  error,
  type,
  inputMode,
  dir,
  ...rest
}) => (
  <div
    className={cn(
      block ? 'flex' : 'inline-flex',
      'relative items-center justify-between gap-2',
      'relative inset-0 w-full bg-background-primary border border-border-disabled',
      'focus-within:outline-0 focus-within:border-border-brand has-[[data-focus]]:border-border-brand',
      'has-[[data-disabled]]:cursor-not-allowed has-[[data-disabled]]:bg-background-disabled has-[[data-disabled]]:text-content-tertiary',
      'has-[[data-error]]:border-content-negative',
      'transition-colors w-full max-w-full max-h-full',
      size === 16 && ['h-16 rounded-xl', fab ? 'text-center w-16' : 'px-4'],
      size === 12 && ['h-12 rounded-xl', fab ? 'text-center w-12' : 'px-3'],
      size === 10 && ['h-10 rounded-md', fab ? 'text-center w-10' : 'px-3'],
      size === 8 && ['h-8 rounded-sm', fab ? 'text-center w-8' : 'px-2'],
      className,
    )}
    dir={dir}
  >
    {prefix}
    <BaseInput
      className={cn(
        'size-full border-0 !outline-0 !ring-0 grow',
        'placeholder:text-content-primary/25',
        size === 16 && 'text-lg placeholder-shown:text-base',
        size === 12 && 'text-base placeholder-shown:text-sm',
        size === 10 && 'text-base placeholder-shown:text-sm',
        size === 8 && 'text-sm placeholder-shown:text-xs',
      )}
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      type={type}
      pattern={type === 'number' || type === 'tel' ? '[0-9]*' : undefined}
      inputMode={
        inputMode ??
        (type === 'number'
          ? 'numeric'
          : ['email', 'search', 'tel', 'url'].includes(type ?? 'text')
            ? ((type as never) ?? 'text')
            : 'text')
      }
      {...(error && {
        'data-error': true,
      })}
      autoComplete="off"
      dir={dir}
      {...rest}
    />
    {suffix}
  </div>
);
